/**
 * Staff portal shell — workflow-ordered sidebar + GOVERN group (plan §3.1).
 * Auth: platform staff session; guards redirect to /staff/login.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Banknote, ClipboardCheck, FileSearch, Landmark, LayoutDashboard, PhoneCall, Receipt, Send, TrendingUp,
  Loader2, LogOut, MapPinned, Scale, ShieldCheck, Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { staffApi, type StaffUser } from "@/platform/staffApi";
import { tokenStore } from "@/platform/api";

const StaffContext = createContext<StaffUser | null>(null);
export const useStaff = () => useContext(StaffContext);

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  national?: boolean;
}

const NAV: { group: string; items: NavItem[] }[] = [
  { group: "Overview", items: [
    { to: "/staff", label: "Dashboard", icon: LayoutDashboard, end: true },
  ]},
  { group: "Workflow", items: [
    { to: "/staff/applications", label: "Applications", icon: ClipboardCheck },
    { to: "/staff/kyc", label: "KYC Review", icon: ShieldCheck },
    { to: "/staff/disbursements", label: "Disbursements", icon: Send },
    { to: "/staff/collections", label: "Collections", icon: PhoneCall },
    { to: "/staff/reconciliation", label: "Reconciliation", icon: Scale },
  ]},
  { group: "Portfolio", items: [
    { to: "/staff/loans", label: "Loans", icon: Banknote },
    { to: "/staff/payments", label: "Payments", icon: Receipt },
    { to: "/staff/clients", label: "Clients", icon: Users },
    { to: "/staff/sales", label: "Sales", icon: TrendingUp },
  ]},
  { group: "Govern", items: [
    { to: "/staff/audit", label: "Audit Trail", icon: FileSearch, national: true },
    { to: "/staff/products", label: "Loan Products", icon: Scale, national: true },
    { to: "/staff/regions", label: "Staff & Regions", icon: MapPinned, national: true },
    { to: "/staff/security", label: "Security", icon: ShieldCheck },
  ]},
];

export default function StaffShell() {
  const navigate = useNavigate();
  const [user, setUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tokenStore.hasSession) {
      navigate("/staff/login");
      return;
    }
    staffApi.me()
      .then((me) => {
        if (me.role === "customer") navigate("/staff/login");
        else setUser(me);
      })
      .catch(() => navigate("/staff/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading || !user) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const isNational = ["admin", "national_manager"].includes(user.role);

  return (
    <StaffContext.Provider value={user}>
      <div className="flex min-h-screen bg-muted/30">
        <aside className="flex w-60 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground">
          <div className="flex items-center gap-2 border-b px-4 py-4">
            <Landmark className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-semibold leading-none">Ngwees Capital</p>
              <p className="mt-0.5 text-xs text-muted-foreground capitalize">{user.role.replace("_", " ")}</p>
            </div>
          </div>
          <nav className="flex-1 space-y-4 overflow-y-auto p-3">
            {NAV.map((section) => {
              const items = section.items.filter((i) => !i.national || isNational);
              if (!items.length) return null;
              return (
                <div key={section.group}>
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.group}
                  </p>
                  {items.map(({ to, label, icon: Icon, end }) => (
                    <NavLink key={to} to={to} end={end}
                      className={({ isActive }) =>
                        `flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                          isActive ? "bg-primary/10 font-medium text-primary" : "text-foreground/80 hover:bg-muted"
                        }`}>
                      <Icon className="h-4 w-4" /> {label}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
          <div className="border-t p-3">
            <Button variant="ghost" size="sm" className="w-full justify-start"
              onClick={async () => { await staffApi.logout(); navigate("/staff/login"); }}>
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </StaffContext.Provider>
  );
}
